/**
 * Rich renderer for blog post content.
 * Handles: **bold**, *italic*, ***bold+italic***, [sm|lg|xl]…[/…] size wrappers,
 * [link text](/path or https://url) markdown-style links,
 * plus the site-specific heading/bullet auto-styling and per-post font family.
 * Shared between the public BlogPostPage and the admin live-preview panel.
 */

import { Link as RouterLink } from "react-router-dom";

const renderLink = (label, href, key) => {
  const isExternal = /^https?:\/\//i.test(href) || href.startsWith("//");
  const children = applyInline(label, `${key}-r`);
  if (isExternal) {
    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors"
      >
        {children}
      </a>
    );
  }
  return (
    <RouterLink
      key={key}
      to={href}
      className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors"
    >
      {children}
    </RouterLink>
  );
};

const applyInline = (text, keyPrefix = "i") => {
  if (!text) return text;
  const regex =
    /(\*\*\*[\s\S]+?\*\*\*|\*\*[\s\S]+?\*\*|\*[^*\n]+?\*|\[(?:sm|lg|xl)\][\s\S]+?\[\/(?:sm|lg|xl)\]|\[[^\]\n]+?\]\([^)\s]+?\))/g;
  const parts = text.split(regex);
  return parts.map((chunk, i) => {
    if (!chunk) return null;
    const key = `${keyPrefix}-${i}`;
    if (/^\*\*\*[\s\S]+\*\*\*$/.test(chunk)) {
      return (
        <strong key={key}>
          <em>{applyInline(chunk.slice(3, -3), `${key}-r`)}</em>
        </strong>
      );
    }
    if (/^\*\*[\s\S]+\*\*$/.test(chunk)) {
      return <strong key={key}>{applyInline(chunk.slice(2, -2), `${key}-r`)}</strong>;
    }
    if (/^\*[^*\n]+\*$/.test(chunk)) {
      return <em key={key}>{applyInline(chunk.slice(1, -1), `${key}-r`)}</em>;
    }
    const linkMatch = chunk.match(/^\[([^\]\n]+?)\]\(([^)\s]+?)\)$/);
    if (linkMatch) {
      return renderLink(linkMatch[1], linkMatch[2], key);
    }
    const sizeMatch = chunk.match(/^\[(sm|lg|xl)\]([\s\S]+)\[\/(sm|lg|xl)\]$/);
    if (sizeMatch && sizeMatch[1] === sizeMatch[3]) {
      const size = sizeMatch[1];
      const inner = sizeMatch[2];
      const cls =
        size === "sm" ? "text-sm" :
        size === "lg" ? "text-lg" :
        "text-2xl font-medium";
      return (
        <span key={key} className={cls}>
          {applyInline(inner, `${key}-r`)}
        </span>
      );
    }
    return <span key={key}>{chunk}</span>;
  });
};

const parseHeading = (line) => {
  const t = line.trim();
  const md = t.match(/^(#{2,3})\s+(.+)$/);
  if (md) return { level: md[1].length, text: md[2] };
  const html = t.match(/^<h([23])[^>]*>([\s\S]*?)<\/h\1>$/i);
  if (html) return { level: Number(html[1]), text: html[2].replace(/<[^>]+>/g, "") };
  return null;
};

// Turn cross-paragraph wrappers into per-paragraph wrappers so each paragraph
// inside gets styled. Handles arbitrary nesting order by iterating until stable.
// Order matters: process innermost markers first (size), then bold+italic,
// then bold, then italic — so an outer split doesn't orphan inner markers.
const normalizeCrossParagraphWrappers = (content) => {
  const wrap = (open, close, inner) =>
    inner
      .split(/\n\n+/)
      .map((p) => (p.trim() ? `${open}${p}${close}` : p))
      .join("\n\n");
  const passes = [
    // [sm|lg|xl] first (innermost styling)
    (s) => s.replace(/\[(sm|lg|xl)\]([\s\S]+?)\[\/\1\]/g, (_m, size, inner) =>
      inner.includes("\n\n") ? wrap(`[${size}]`, `[/${size}]`, inner) : _m
    ),
    // ***bold+italic***
    (s) => s.replace(/\*\*\*([\s\S]+?)\*\*\*/g, (_m, inner) =>
      inner.includes("\n\n") ? wrap("***", "***", inner) : _m
    ),
    // **bold**
    (s) => s.replace(/\*\*([\s\S]+?)\*\*/g, (_m, inner) =>
      inner.includes("\n\n") ? wrap("**", "**", inner) : _m
    ),
  ];
  let out = content;
  // Iterate up to 4 times until content stops changing (handles deep nesting)
  for (let i = 0; i < 4; i++) {
    const prev = out;
    for (const pass of passes) out = pass(out);
    if (out === prev) break;
  }
  return out;
};

/**
 * <PostContent content={...} contentFont={...} />
 * Renders a blog post body with the same styling used on the public post page.
 */
const PostContent = ({ content = "", contentFont = "" }) => {
  const normalized = normalizeCrossParagraphWrappers(content);
  const sections = normalized.split("\n\n");
  const fontClass =
    contentFont === "serif"
      ? "font-serif"
      : contentFont === "mono"
      ? "font-mono"
      : contentFont === "sans"
      ? "font-sans"
      : "";

  return (
    <div className={fontClass}>
      {sections.map((section, sectionIdx) => {
        const lines = section.split("\n");
        const sectionKey = `s-${sectionIdx}-${section
          .slice(0, 20)
          .replace(/\s+/g, "_")}`;

        return (
          <div key={sectionKey} className="mb-6">
            {lines.map((line, lineIdx) => {
              const lineKey = `${sectionKey}-l-${lineIdx}-${line
                .slice(0, 20)
                .replace(/\s+/g, "_")}`;

              const heading = parseHeading(line);
              if (heading) {
                const Tag = heading.level === 2 ? "h2" : "h3";
                return (
                  <Tag
                    key={lineKey}
                    className={
                      heading.level === 2
                        ? "font-serif text-3xl md:text-4xl font-semibold text-foreground leading-tight mt-12 mb-5"
                        : "font-serif text-2xl md:text-[1.7rem] font-semibold text-foreground leading-snug mt-10 mb-4"
                    }
                    data-testid={`post-heading-h${heading.level}`}
                  >
                    {applyInline(heading.text, lineKey)}
                  </Tag>
                );
              }
              if (
                line.match(
                  /^(My Specialties|Conditions I Work With|Approaches and Types of Therapy I Provide|Why ["“']Trying Harder["”']|What Becomes Possible|This Is for You If|The Cost of Waiting|What Working Together Looks Like|Your Next Step)/i
                )
              ) {
                return (
                  <h3
                    key={lineKey}
                    className="font-serif text-2xl font-semibold text-foreground mt-10 mb-4"
                  >
                    {applyInline(line, lineKey)}
                  </h3>
                );
              } else if (
                line.match(
                  /^(Anxiety|Burnout|Grief|PTSD|Self-Esteem|ADHD|Adjustment|Body Image|Depression|Insomnia|Stress|Psychedelic|Ketamine|Coaching|Cognitive|Compassion|EMDR|Gestalt|Group|Holistic|Integrative|Internal|Mindfulness|Psychodynamic|Sensorimotor|Solution|Somatic|Therapeutic|Trauma)/i
                ) &&
                line.length < 120
              ) {
                return (
                  <div key={lineKey} className="flex items-center gap-2 py-1">
                    <span className="w-2 h-2 rounded-full bg-accent"></span>
                    <span className="text-muted-foreground">
                      {applyInline(line, lineKey)}
                    </span>
                  </div>
                );
              } else if (line.trim()) {
                return (
                  <p
                    key={lineKey}
                    className="text-foreground/90 leading-relaxed mb-4"
                  >
                    {applyInline(line, lineKey)}
                  </p>
                );
              }
              return null;
            })}
          </div>
        );
      })}
    </div>
  );
};

export default PostContent;
